/*
 * Copyright 2026 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import importHTML from '../components/richText/customImport';

const HAS_LIST = /<(?:ul|ol|li)(?:\s|>)/i;
const BETWEEN_BLOCK_TAGS =
  /(<\/?(?:ul|ol|li|p)\b[^>]*>)[\t\r\n ]+(?=<\/?(?:ul|ol|li|p)\b[^>]*>)/gi;
const BEFORE_OPEN_LIST = /[\t\r\n ]+(?=<(?:ul|ol)\b[^>]*>)/gi;
const AFTER_CLOSE_LIST = /(<\/(?:ul|ol)>)[\t\r\n ]+/gi;
const AFTER_LINE_BREAK = /(<br\s*\/?>)\r?\n/gi;

/**
 * Removes serializer formatting whitespace around semantic list elements.
 * Whitespace within text or between inline elements remains unchanged.
 *
 * @param {string} content HTML content.
 * @return {string} Compacted list markup.
 */
function compactListMarkup(content = '') {
  if (typeof content !== 'string' || !HAS_LIST.test(content)) {
    return content;
  }

  let compacted = content;
  let previous;

  do {
    previous = compacted;
    compacted = compacted
      .replace(BETWEEN_BLOCK_TAGS, '$1')
      .replace(BEFORE_OPEN_LIST, '')
      .replace(AFTER_CLOSE_LIST, '$1')
      .replace(AFTER_LINE_BREAK, '$1');
  } while (compacted !== previous);

  return compacted;
}

export function getCaretCompatibleListMarkup(
  content = '',
  {
    defaultFontSizeCss,
    defaultLineHeight,
    includeListMarkerMetrics = false,
  } = {}
) {
  const compactedContent = compactListMarkup(content);
  if (!HAS_LIST.test(compactedContent)) {
    return compactedContent;
  }

  const container = document.createElement('div');
  container.innerHTML = compactedContent;
  if (defaultFontSizeCss !== undefined) {
    container.querySelectorAll('p').forEach((paragraph) => {
      // Keep empty paragraph placeholders on the box's existing metrics.
      if (
        !paragraph.textContent &&
        paragraph.querySelectorAll('br').length < 2
      ) {
        return;
      }
      const materializeLeaves = (node, fontSize, lineHeight) => {
        if (node.nodeType === Node.TEXT_NODE || node.nodeName === 'BR') {
          const run = document.createElement('span');
          run.style.fontSize = fontSize;
          run.style.lineHeight = String(lineHeight);
          node.replaceWith(run);
          run.appendChild(node);
          return;
        }
        Array.from(node.childNodes).forEach((child) =>
          materializeLeaves(
            child,
            node.style?.fontSize || fontSize,
            node.style?.lineHeight || lineHeight
          )
        );
      };
      materializeLeaves(paragraph, defaultFontSizeCss, defaultLineHeight);
      // Match SVG and the active editor: inline runs own nonempty paragraph
      // line boxes, including when their sizes are below the box default.
      paragraph.style.fontSize = '0px';
      paragraph.style.lineHeight = '0';
    });
  }
  const blocks = Array.from(container.querySelectorAll('li, p')).filter(
    (block) => block.tagName === 'LI' || !block.closest('li')
  );

  // Use Draft's import contract for offsets, including its empty placeholders
  // and closing paragraph breaks. Range.toString() does not count BRs.
  const importedBlocks = importHTML(compactedContent).getBlocksAsArray();
  blocks.forEach((block, index) => {
    const softBreakCount =
      importedBlocks[index]?.getText().split('\n').length - 1 || 0;
    Array.from(block.querySelectorAll('br'))
      .filter((br) => br.closest('li, p') === block)
      .slice(0, softBreakCount)
      .forEach((br) => {
        const delimiter = document.createElement('span');
        delimiter.setAttribute('aria-hidden', 'true');
        delimiter.style.display = 'none';
        delimiter.textContent = '\n';
        br.before(delimiter);
      });
  });

  blocks.slice(1).forEach((block) => {
    const delimiter = document.createElement('span');
    delimiter.setAttribute('aria-hidden', 'true');
    delimiter.setAttribute('data-draft-block-delimiter', 'true');
    delimiter.style.display = 'none';
    delimiter.textContent = '\n';
    block.insertBefore(delimiter, block.firstChild);
  });

  if (includeListMarkerMetrics) {
    container.querySelectorAll('li').forEach((item) => {
      const markerMetrics = document.createElement('span');
      markerMetrics.setAttribute('data-caret-list-marker', '');
      markerMetrics.setAttribute('aria-hidden', 'true');
      const delimiter = item.querySelector(
        ':scope > [data-draft-block-delimiter]'
      );
      if (delimiter) {
        delimiter.after(markerMetrics);
      } else {
        item.prepend(markerMetrics);
      }
    });
  }

  return container.innerHTML;
}

export default compactListMarkup;
